import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Snackbar,   
  Alert 
} from '@mui/material';
import {
  People,
  LocalHospital,
  Person,
  Engineering,
  Visibility,
  Block,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const UsersMonitoring = () => {
  const [users, setUsers] = useState([]);
  const [cabinets, setCabinets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    cabinet: 'all',
    role: 'all',
    search: ''
  });
  const [stats, setStats] = useState({
    totalUsers: 0,
    veterinaires: 0,
    secretaires: 0,
    techniciens: 0
  });

const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

const showSnackbar = (message, severity) => {
  setSnackbar({ open: true, message, severity });
};

const handleCloseSnackbar = () => {
  setSnackbar({ ...snackbar, open: false });
};
  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    calculateStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);
const handleViewUser = (user) => {
  alert(`Détails de ${user.prenom_user} ${user.nom_user}\n\nLogin: ${user.login}\nRôle: ${user.Role?.description_role || 'N/A'}\nCabinet: ${user.cabinet_nom}`);
};

const handleToggleUserStatus = async (user) => {
  const action = user.actif ? 'désactiver' : 'activer';
  if (!window.confirm(`Voulez-vous vraiment ${action} cet utilisateur : ${user.prenom_user} ${user.nom_user} ?`)) {
    return;
  }

  try {
    const token = localStorage.getItem('token');
    await axios.put(
      `${API_URL}/super-admin/users/${user.id_user}/toggle-status`,
      { actif: !user.actif },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    showSnackbar(`Utilisateur ${action === 'désactiver' ? 'désactivé' : 'activé'} avec succès`, 'success');
    loadData();
  } catch (error) {
    showSnackbar('Erreur lors de la modification', 'error');
  }
};

const loadData = async () => {
  try {
    setLoading(true);
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    // Charger tous les cabinets
    const cabinetsRes = await axios.get(`${API_URL}/super-admin/cabinets`, config);
    const cabinetsData = cabinetsRes.data.data || [];
    setCabinets(cabinetsData);

    // Pour chaque cabinet, charger ses utilisateurs via l'API admin
    const allUsers = [];
    
    // D'abord, essayer de récupérer tous les utilisateurs directement
    try {
      const usersRes = await axios.get(`${API_URL}/super-admin/users`, config);
      allUsers.push(...usersRes.data.data || []);
    } catch (error) {
      // Si la route n'existe pas, charger depuis les cabinets
      for (const cabinet of cabinetsData) {
        if (cabinet.Utilisateurs && cabinet.Utilisateurs.length > 0) {
          cabinet.Utilisateurs.forEach(user => {
            allUsers.push({
              ...user,
              cabinet_nom: cabinet.nom_cabinet,
              id_cabinet: cabinet.id_cabinet
            });
          });
        }
      }
    }
    
    console.log('Utilisateurs chargés:', allUsers); // Pour debug
    setUsers(allUsers);
  } catch (error) {
    console.error('Erreur chargement:', error);
    showSnackbar('Erreur lors du chargement', 'error');
  } finally {
    setLoading(false);
  }
};

  const calculateStats = () => {
    const filtered = getFilteredUsers();
    setStats({
      totalUsers: filtered.length,
      veterinaires: filtered.filter(u => u.role_id === 2).length,
      secretaires: filtered.filter(u => u.role_id === 3).length,
      techniciens: filtered.filter(u => u.role_id === 4).length
    });
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const getFilteredUsers = () => {
    return users.filter(user => {
      const matchCabinet = filters.cabinet === 'all' || user.id_cabinet === parseInt(filters.cabinet);
      const matchRole = filters.role === 'all' || user.role_id === parseInt(filters.role);
      const matchSearch = !filters.search || 
        user.nom_user?.toLowerCase().includes(filters.search.toLowerCase()) ||
        user.prenom_user?.toLowerCase().includes(filters.search.toLowerCase()) ||
        user.login?.toLowerCase().includes(filters.search.toLowerCase());
      
      return matchCabinet && matchRole && matchSearch;
    });
  };

  const getRoleInfo = (roleId) => {
    const roles = {
      1: { name: 'Super Admin', color: 'error', icon: <Person /> },
      2: { name: 'Vétérinaire', color: 'error', icon: <LocalHospital /> },
      3: { name: 'Secrétaire', color: 'primary', icon: <Person /> },
      4: { name: 'Technicien', color: 'success', icon: <Engineering /> }
    };
    return roles[roleId] || roles[3];
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredUsers = getFilteredUsers();

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" gutterBottom>
        <People sx={{ mr: 1, verticalAlign: 'middle' }} />
        Monitoring des Utilisateurs
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Vue d'ensemble de tous les utilisateurs de tous les cabinets
      </Typography>

      {/* Statistiques */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div>
                  <Typography color="text.secondary" variant="body2">
                    Total Utilisateurs
                  </Typography>
                  <Typography variant="h4">{stats.totalUsers}</Typography>
                </div>
                <People color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div>
                  <Typography color="text.secondary" variant="body2">
                    Vétérinaires
                  </Typography>
                  <Typography variant="h4">{stats.veterinaires}</Typography>
                </div>
                <LocalHospital color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div>
                  <Typography color="text.secondary" variant="body2">
                    Secrétaires
                  </Typography>
                  <Typography variant="h4">{stats.secretaires}</Typography>
                </div>
                <Person color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div>
                  <Typography color="text.secondary" variant="body2">
                    Techniciens
                  </Typography>
                  <Typography variant="h4">{stats.techniciens}</Typography>
                </div>
                <Engineering color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtres */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Rechercher"
              variant="outlined"
              size="small"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Nom, prénom ou login..."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Cabinet"
              size="small"
              value={filters.cabinet}
              onChange={(e) => handleFilterChange('cabinet', e.target.value)}
            >
              <MenuItem value="all">Tous les cabinets</MenuItem>
              {cabinets.map((cabinet) => (
                <MenuItem key={cabinet.id_cabinet} value={cabinet.id_cabinet}>
                  {cabinet.nom_cabinet}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Rôle"
              size="small"
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
            >
              <MenuItem value="all">Tous les rôles</MenuItem>
              <MenuItem value="2">Vétérinaire</MenuItem>
              <MenuItem value="3">Secrétaire</MenuItem>
              <MenuItem value="4">Technicien</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Tableau des utilisateurs */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Cabinet</strong></TableCell>
              <TableCell><strong>Nom</strong></TableCell>
              <TableCell><strong>Prénom</strong></TableCell>
              <TableCell><strong>Login</strong></TableCell>
              <TableCell><strong>Rôle</strong></TableCell>
              <TableCell><strong>Statut</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary">
                    Aucun utilisateur trouvé avec ces filtres
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => {
                const roleInfo = getRoleInfo(user.role_id);
                return (
                  <TableRow key={user.id_user} hover>
                    <TableCell>{user.id_user}</TableCell>
                    <TableCell>
                      <Chip label={user.cabinet_nom} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell><strong>{user.nom_user}</strong></TableCell>
                    <TableCell>{user.prenom_user}</TableCell>
                    <TableCell>{user.login}</TableCell>
                    <TableCell>
                      <Chip
                        icon={roleInfo.icon}
                        label={roleInfo.name}
                        color={roleInfo.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.is_super_admin ? (
                      <Chip
                        icon={<CheckCircle />}
                        label="Super Admin"
                        color="warning"
                        size="small"
                        />
                       ) : (
                      <Chip
                        icon={user.actif === false ? <Cancel /> : <CheckCircle />}
                        label={user.actif === false ? "Inactif" : "Actif"}
                        color={user.actif === false ? "error" : "success"}
                        size="small"
                      />
                    )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Voir détails">
                     <IconButton size="small" color="primary" onClick={() => handleViewUser(user)}>
                     <Visibility />
                     </IconButton>
                      </Tooltip>
                      {!user.is_super_admin && (
                        <Tooltip title="Désactiver">
                        <IconButton size="small" color="error" onClick={() => handleToggleUserStatus(user)}>
                        <Block />
                        </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          Affichage de {filteredUsers.length} utilisateur(s) sur {users.length} total
        </Typography>
      </Box>
      
      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UsersMonitoring;